
import { motion } from 'framer-motion';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

interface HeroProps {
  language: 'de' | 'ru';
  onPlayMusic?: () => void;
}

export default function Hero({ language, onPlayMusic }: HeroProps) {
  const heroAnim = useScrollAnimation({ threshold: 0.2 });

  const content = {
    de: {
      nav: [
        { label: 'Home', id: 'home' },
        { label: 'Über Peter', id: 'about' },
        { label: 'Lieder-Archiv', id: 'songs' },
        { label: 'Beratung', id: 'consulting' },
        { label: 'Kontakt', id: 'contact' }
      ],
      title: 'Peters Klangwelt',
      subtitle: 'Romantik, Philosophie & Heimat in Wort und Bild',
      button: 'Musik einschalten ♫',
      buttonStop: 'Musik pausieren'
    },
    ru: {
      nav: [
        { label: 'Главная', id: 'home' },
        { label: 'О Питере', id: 'about' },
        { label: 'Архив песен', id: 'songs' },
        { label: 'Консультации', id: 'consulting' },
        { label: 'Контакт', id: 'contact' }
      ],
      title: 'Мир Питера',
      subtitle: 'Романтика, философия и родина в слове и образе',
      button: 'Включить музыку ♫',
      buttonStop: 'Остановить музыку'
    }
  };

  const t = content[language];

  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0, transition: { duration: 1, ease: 'easeOut' } }
  };

  const fadeInScale = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 1, ease: 'easeOut', delay: 0.3 } }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/8743e5a2ae59ed09db57a41f3414356d.jpg"
          alt="Hero Background"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
      </div>

      {/* Content */}
      <motion.div 
        ref={heroAnim.ref}
        initial="hidden"
        animate={heroAnim.isVisible ? "visible" : "hidden"}
        className="relative z-10 text-center max-w-5xl mx-auto px-4"
      >
        <motion.div variants={fadeInUp}>
          <h1 className="font-serif text-6xl md:text-8xl font-bold text-white mb-6 drop-shadow-2xl">
            {t.title}
          </h1>
          <p className="font-serif text-2xl md:text-3xl text-[#C5B358] mb-8 drop-shadow-lg">
            {t.subtitle}
          </p>
        </motion.div>

        <motion.div variants={fadeInScale}>
          <button 
            onClick={onPlayMusic}
            className="px-12 py-5 bg-[#8b2332] text-white font-serif text-xl rounded-full border-2 border-[#C5B358] hover:bg-[#6d1a27] transition-all duration-300 shadow-2xl hover:shadow-3xl hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-music-2-line text-2xl mr-3"></i>
            {t.button}
          </button>
        </motion.div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 1, repeat: Infinity, repeatType: 'reverse' }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10"
      >
        <i className="ri-arrow-down-line text-4xl text-white/70"></i>
      </motion.div>
    </section>
  );
}
